import React, { useState, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import {
  colors,
  IconCall,
  IconEMail,
  IconExternal,
  IconIllustrativeWarningAlt,
  IconIllustrativeOk,
} from '@blocket/blocket-ui'
import { setWarningModalType } from 'reduxBits/chat/actions'
import { actionCreators as accountActions } from 'reduxBits/account/actions'
import { copyToClipboard } from 'utils/clipboard'
import staticFilesUrl from 'constants/staticFiles'
import useOnMount from 'customHooks/useOnMount'
import {
  trackModalView,
  trackingWarningModalAction,
  trackingWarningModalReadMoreLink,
  trackActivateEmailCopy,
} from 'tracking/chat'
import formatHref from 'components/pagesContent/Chat/utils/fraudPrevention/formatHref'
import IconRotatingBell from 'components/sharedComponents/IconRotatingBell'
import Buttons from './Buttons'
import Link from './Link'
import {
  CloseIcon,
  Content,
  Section,
  Label,
  Text,
  ShieldIcon,
  WarningIcon,
  IconWrapper,
} from './styles.js'

const WarningModalContent = ({
  type,
  match = '',
  viewTracking,
  closeModal,
  selectedChannelUrl,
  sendMessage,
  blockReasonsType = '',
}) => {
  const { uriModeration, blockReasons } = useSelector((state) => state.chat.clientSettings)
  const {
    error: emailNotificationError,
    propertyMap: { mcSendEmailCopy: emailNotificationEnabled },
  } = useSelector((state) => state.account.user)
  const dispatch = useDispatch()

  const [content, setContent] = useState({
    label: 'Var försiktig',
    text: `Utanför Blocket är risken större att utsättas för bedrägeriförsök. 
    Vi rekommenderar att ha kvar konversationen här i Meddelanden.`,
    icon: <ShieldIcon src={`${staticFilesUrl}/assets/images/blocket-shield.svg`} alt="logo" />,
    closeIcon: null,
    buttons: null,
    link: null,
    set: false,
  })

  const [updateModal, setUpdateModal] = useState(false)

  const formatContent = useCallback(
    ({ data, primaryButtonData, secondaryButtonData, link, icon, closeIcon }) => {
      const secondaryButtonProps = secondaryButtonData && {
        secondaryButton: {
          text: data?.secondaryButtonText || secondaryButtonData.text,
          onClick: secondaryButtonData.onClick,
        },
      }

      return {
        ...content,
        label: data.title,
        text: data.content || data.body,
        buttons: (
          <Buttons
            primaryButton={{
              text: primaryButtonData.text || data.primaryButtonText || data.cta,
              ...(match && { href: formatHref(type, match) }),
              externalLink: primaryButtonData.externalLink,
              icon: primaryButtonData.icon,
              onClick: primaryButtonData.onClick,
            }}
            {...secondaryButtonProps}
          />
        ),
        ...(icon && { icon }),
        ...(link && {
          link: <Link text={link.text} href={link.url} onClick={link.onClick} />,
        }),
        ...(closeIcon && { closeIcon: <CloseIcon onClick={closeModal} /> }),
        set: true,
      }
    },
    [closeModal, content, match, type],
  )

  const emailNotificationConfirmation = useCallback(
    (status) => {
      const iconSize = 56
      const iconProps = {
        width: iconSize,
        height: iconSize,
      }

      return formatContent({
        data: {
          title: status === 'success' ? 'Klart!' : status === 'error' && 'Åh nej!',
          body:
            status === 'success'
              ? 'Nu får du notiser via mejl när någon skriver ett meddelande till dig!'
              : status === 'error' &&
                'Något gick fel, men du kan alltid ändra notiserna under kontoinställningar.',
        },
        closeIcon: true,
        primaryButtonData: {
          text: 'Okej',
          onClick: closeModal,
        },
        icon: (
          <IconWrapper>
            {status === 'success' ? (
              <IconIllustrativeOk {...iconProps} />
            ) : (
              status === 'error' && <IconIllustrativeWarningAlt {...iconProps} />
            )}
          </IconWrapper>
        ),
      })
    },
    [closeModal, formatContent],
  )

  const copyLink = useCallback(async () => {
    const copyLink = await copyToClipboard(match)
    if (copyLink) {
      closeModal()
    }
  }, [closeModal, match])

  const primaryExternalContactCallback = useCallback(() => {
    trackingWarningModalAction(type, selectedChannelUrl, 'primary')
    closeModal()
  }, [closeModal, selectedChannelUrl, type])

  const secondaryExternalContactCallback = useCallback(() => {
    trackingWarningModalAction(type, selectedChannelUrl, 'secondary')
    copyLink()
    closeModal()
  }, [closeModal, copyLink, selectedChannelUrl, type])

  const getContent = useCallback(
    (type) => {
      switch (type) {
        case 'send_message_chat':
          return formatContent({
            data: uriModeration.emailWarning.outgoingMessageModal,
            primaryButtonData: {
              onClick: (e) => {
                sendMessage(e)
                trackingWarningModalAction('', selectedChannelUrl, 'send')
                closeModal()
              },
            },
            secondaryButtonData: {
              onClick: () => {
                closeModal()
                trackingWarningModalAction('', selectedChannelUrl, 'edit')
              },
            },
          })
        case 'phone': {
          const { incomingMessageModal, readMoreLink } = uriModeration.phoneWarning
          return formatContent({
            data: incomingMessageModal,
            closeIcon: true,
            link: {
              ...readMoreLink,
              onClick: () => trackingWarningModalReadMoreLink(type, selectedChannelUrl),
            },
            primaryButtonData: {
              icon: (
                <IconCall
                  height={20}
                  width={20}
                  style={{ marginRight: 8, color: colors.white100 }}
                />
              ),
              onClick: primaryExternalContactCallback,
            },
            secondaryButtonData: {
              onClick: secondaryExternalContactCallback,
            },
          })
        }
        case 'mail': {
          const { incomingMessageModal, readMoreLink } = uriModeration.emailWarning
          return formatContent({
            data: incomingMessageModal,
            closeIcon: true,
            link: {
              ...readMoreLink,
              onClick: () => trackingWarningModalReadMoreLink(type, selectedChannelUrl),
            },
            primaryButtonData: {
              icon: (
                <IconEMail
                  height={20}
                  width={20}
                  style={{ marginRight: 8, color: colors.white100 }}
                />
              ),
              onClick: primaryExternalContactCallback,
            },
            secondaryButtonData: {
              onClick: secondaryExternalContactCallback,
            },
          })
        }
        case 'link': {
          const { incomingMessageModal, readMoreLink } = uriModeration.linkWarning
          return formatContent({
            data: incomingMessageModal,
            closeIcon: true,
            link: {
              ...readMoreLink,
              onClick: () => trackingWarningModalReadMoreLink(type, selectedChannelUrl),
            },
            primaryButtonData: {
              icon: (
                <IconExternal
                  height={20}
                  width={20}
                  style={{ marginRight: 8, color: colors.white100 }}
                />
              ),
              externalLink: true,
              onClick: primaryExternalContactCallback,
            },
            secondaryButtonData: {
              onClick: secondaryExternalContactCallback,
            },
          })
        }
        case 'block_reasons': {
          const data = blockReasons.find((blockReason) => blockReason.type === blockReasonsType)
          return formatContent({
            data,
            closeIcon: true,
            link: data.link,
            primaryButtonData: {
              onClick: closeModal,
            },
            ...(blockReasonsType === '400401_failure' && { icon: <WarningIcon /> }),
          })
        }
        case 'default_error': {
          return formatContent({
            data: {
              body: 'Just nu går det tyvärr inte att skicka meddelandet. Försök igen lite senare.',
              cta: 'Okej',
              title: 'Oj, något gick fel!',
            },
            closeIcon: true,
            primaryButtonData: {
              onClick: closeModal,
            },
            icon: <WarningIcon />,
          })
        }
        case 'send-email-copy': {
          return formatContent({
            data: {
              title: 'Få notiser via mejl?',
              body:
                'Få notiser i mejlen när någon skickar meddelande till dig. Du kan ändra detta när du vill i kontoinställningar.',
            },
            closeIcon: true,
            primaryButtonData: {
              text: 'Aktivera',
              onClick: () => {
                trackActivateEmailCopy()

                dispatch(
                  accountActions.updateAccountSettings({
                    mc_send_email_copy: true,
                  }),
                )

                setUpdateModal(true)
              },
            },
            secondaryButtonData: {
              text: 'Nej tack',
              onClick: closeModal,
            },
            icon: (
              <IconWrapper>
                <IconRotatingBell />
              </IconWrapper>
            ),
          })
        }
        default:
          break
      }
    },
    [
      formatContent,
      uriModeration?.emailWarning,
      uriModeration?.phoneWarning,
      uriModeration?.linkWarning,
      sendMessage,
      selectedChannelUrl,
      closeModal,
      primaryExternalContactCallback,
      secondaryExternalContactCallback,
      blockReasons,
      blockReasonsType,
      dispatch,
    ],
  )

  useOnMount(() => {
    if (viewTracking) {
      viewTracking()
    }
  })

  useEffect(() => {
    if (type && !content.set) {
      dispatch(setWarningModalType(type))
      setContent(getContent(type))
    }
  }, [type, dispatch, getContent, content])

  // used for updating current modal with new content
  useEffect(() => {
    if (updateModal) {
      if (type === 'send-email-copy') {
        if ((emailNotificationError && !emailNotificationEnabled) || emailNotificationEnabled) {
          const confirmationStatus =
            emailNotificationError && !emailNotificationEnabled
              ? 'error'
              : emailNotificationEnabled && 'success'

          if (confirmationStatus === 'error') {
            trackModalView({
              objectId: 'email_optin_modal_fail',
              eventName: 'Email optin modal failed',
            })
          } else if (confirmationStatus === 'success') {
            trackModalView({
              objectId: 'email_optin_modal_success',
              eventName: 'Email optin modal success.',
            })
          }
          setUpdateModal(false)
          setContent(emailNotificationConfirmation(confirmationStatus))
        }
      }
    }
  }, [
    closeModal,
    emailNotificationConfirmation,
    formatContent,
    emailNotificationEnabled,
    emailNotificationError,
    type,
    updateModal,
  ])

  return (
    <Content>
      {content?.closeIcon}
      {content?.icon}
      <Label>{content?.label}</Label>
      <Section>
        <Text>
          {content?.text} {content?.link}
        </Text>
      </Section>
      {content?.buttons}
    </Content>
  )
}

WarningModalContent.propTypes = {
  type: PropTypes.string.isRequired,
  match: PropTypes.string,
  viewTracking: PropTypes.func,
  closeModal: PropTypes.func,
  selectedChannelUrl: PropTypes.string,
  sendMessage: PropTypes.func,
  blockReasonsType: PropTypes.string,
}

export default WarningModalContent
